import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";

import api from "../../services/api";

/**
 * Jump anywhere: Ctrl+K (⌘K on a Mac), or the search button in the top bar.
 *
 * Pages match as you type, locally. From two characters on, the registers are
 * asked too -- clients, threads, people, accounts -- five rows each, in
 * parallel, once typing pauses. Every one of those is an ILIKE with no
 * trigram index, which is why it waits for the pause and asks for five.
 *
 * Arrows move, Enter opens, Esc closes. Staff only, like the panel itself;
 * the server scopes every list anyway.
 */
const SOURCES = [
  {
    key: "clients",
    label: "Mijoz tizimlari",
    path: "/clients/",
    icon: "solar:server-square-outline",
    row: (c) => ({ title: c.name, sub: c.slug, to: `/mijoz-tizimlari/${c.id}` }),
  },
  {
    key: "threads",
    label: "Muhokamalar",
    path: "/threads/",
    icon: "solar:chat-round-line-outline",
    row: (t) => ({
      title: t.title || t.external_object_id,
      sub: `${t.client_name || ""} · ${t.external_object_id}`,
      to: `/muhokamalar/${t.id}`,
    }),
  },
  {
    key: "people",
    label: "Shaxslar",
    path: "/identities/",
    icon: "solar:users-group-rounded-outline",
    row: (p) => ({ title: p.full_name || p.pinfl, sub: p.pinfl, to: `/shaxslar/${p.id}` }),
  },
  {
    key: "accounts",
    label: "Hisoblar",
    path: "/users/",
    icon: "solar:user-id-outline",
    row: (u) => ({
      title: u.full_name || u.email || u.pinfl,
      sub: `${u.role === "staff" ? "Ichki xodim" : "Kabinet"} · ${u.pinfl || ""}`,
      to: `/foydalanuvchilar/${u.role === "staff" ? "panel" : "kabinet"}/${u.id}`,
    }),
  },
];

const CommandPalette = ({ open, onClose, pages }) => {
  const navigate = useNavigate();
  const input = useRef(null);
  const [term, setTerm] = useState("");
  const [found, setFound] = useState({});
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTerm("");
    setFound({});
    setCursor(0);
    // After the dialog paints, or the focus lands on nothing.
    const t = setTimeout(() => input.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const q = term.trim();
    if (!open || q.length < 2) {
      setFound({});
      setBusy(false);
      return undefined;
    }
    let live = true;
    setBusy(true);
    const t = setTimeout(() => {
      Promise.all(
        SOURCES.map((s) =>
          api
            .get(s.path, { params: { q, limit: 5 } })
            .then((res) => [s.key, (res.data.results || []).map(s.row)])
            .catch(() => [s.key, []]),
        ),
      ).then((pairs) => {
        if (!live) return;
        setFound(Object.fromEntries(pairs));
        setBusy(false);
      });
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [term, open]);

  const sections = useMemo(() => {
    const q = term.trim().toLowerCase();
    const pageRows = pages
      .filter((p) => !q || p.label.toLowerCase().includes(q))
      .map((p) => ({ title: p.label, sub: p.group, to: p.to, icon: p.icon }));
    const out = [];
    if (pageRows.length) out.push({ label: "Sahifalar", rows: pageRows });
    for (const s of SOURCES) {
      const rows = (found[s.key] || []).map((r) => ({ ...r, icon: s.icon }));
      if (rows.length) out.push({ label: s.label, rows });
    }
    return out;
  }, [pages, term, found]);

  const flat = sections.flatMap((s) => s.rows);

  useEffect(() => {
    setCursor((c) => Math.min(c, Math.max(0, flat.length - 1)));
  }, [flat.length]);

  if (!open) return null;

  const go = (row) => {
    if (!row) return;
    onClose();
    navigate(row.to);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (flat.length ? (c + 1) % flat.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (flat.length ? (c - 1 + flat.length) % flat.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[cursor]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  let index = -1;
  return (
    <div className="ms-cmdk-scrim" onMouseDown={onClose}>
      <div
        className="ms-cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Tezkor qidiruv"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ms-cmdk-input">
          <Icon icon="tabler:search" />
          <input
            ref={input}
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onKey}
            placeholder="Sahifa, mijoz tizimi, muhokama, shaxs yoki hisob…"
            aria-label="Qidiruv"
          />
          {busy && <span className="ms-spinner ms-spinner--sm" aria-hidden="true" />}
          <kbd>Esc</kbd>
        </div>

        <div className="ms-cmdk-list" role="listbox">
          {sections.length === 0 ? (
            <div className="ms-cmdk-empty">
              {busy ? "Qidirilmoqda…" : "Hech narsa topilmadi"}
            </div>
          ) : (
            sections.map((s) => (
              <div key={s.label} className="ms-cmdk-group">
                <div className="ms-cmdk-group-title">{s.label}</div>
                {s.rows.map((r) => {
                  index += 1;
                  const i = index;
                  return (
                    <button
                      key={`${s.label}-${r.to}`}
                      type="button"
                      role="option"
                      aria-selected={i === cursor}
                      className={`ms-cmdk-row${i === cursor ? " is-active" : ""}`}
                      onMouseMove={() => i !== cursor && setCursor(i)}
                      onClick={() => go(r)}
                    >
                      <Icon icon={r.icon} />
                      <span>
                        <b>{r.title}</b>
                        {r.sub && <small>{r.sub}</small>}
                      </span>
                      <Icon icon="tabler:corner-down-left" className="ms-cmdk-enter" />
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="ms-cmdk-foot">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> tanlash
          </span>
          <span>
            <kbd>Enter</kbd> ochish
          </span>
          <span>
            <kbd>Ctrl</kbd>
            <kbd>K</kbd> istalgan joydan
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
